import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import React from 'react'
import { NextSeo } from 'next-seo'
import { groq } from 'next-sanity'
import { useRouter } from 'next/router'
import { seoImageLoader } from '@portfolio/components'
import {
  DEFAULT_REVALIDATE_TIME,
  LOGO_URL,
  ORGANIZATION_NAME,
  ORGANIZATION_URL,
  ROUTE_BASE_ARTICLE,
} from '@portfolio/configs'
import { PageBuilderSection, PageResponse } from '@portfolio/models'
import { DefaultPage } from '@portfolio/page'
import {
  getClient,
  getRouteByPath,
  getSiteConfiguration,
} from '@portfolio/services/sanity-service'

const InsightEntryPage: NextPage<PageResponse<PageBuilderSection>> = (
  props,
) => {
  const { seo, disallowRobots, path, articleStructuredData } = props
  const { asPath } = useRouter()

  const canonicalPath = asPath?.split('?')[0] || path || ''
  const canonicalUrl = `${process.env.BASE_URL}${canonicalPath}`
  const seoImage = seoImageLoader(seo?.ogImage, seo?.ogAltImage)

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleStructuredData.headline,
    description: articleStructuredData.description,
    image: articleStructuredData.image,
    datePublished: articleStructuredData.datePublished,
    dateModified: articleStructuredData.dateModified,
    author: articleStructuredData.author,
    publisher: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
      url: ORGANIZATION_URL,
      logo: {
        '@type': 'ImageObject',
        url: LOGO_URL,
      },
    },
    mainEntityOfPage: canonicalUrl,
  }

  return (
    <>
      {seo ? (
        <NextSeo
          titleTemplate={seo?.titleTemplate}
          nofollow={disallowRobots}
          noindex={disallowRobots}
          title={seo.metaTitle}
          description={seo.metaDescription}
          canonical={canonicalUrl}
          openGraph={{
            url: canonicalUrl,
            type: 'article',
            title: seo.ogTitle,
            description: seo.ogDescription,
            images: [...(seoImage ? [seoImage] : [])],
          }}
          twitter={{
            cardType: 'summary_large_image',
          }}
        />
      ) : (
        <NextSeo
          noindex={disallowRobots}
          nofollow={disallowRobots}
          canonical={canonicalUrl}
        />
      )}
      {articleJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
      )}
      <DefaultPage {...props} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async ({ defaultLocale }) => {
  const paths: string[] = await getClient(false).fetch(
    groq`*[
      _type == 'article' &&
      !(_id in path('drafts.**')) &&
      defined(path.current)
    ][].path.current`,
  )

  const staticPaths = paths
    .map((path) => path.replace(`${ROUTE_BASE_ARTICLE}/`, ''))
    .filter((entry) => !!entry && !entry.includes('/'))
    .map((entry) => ({
      params: { entry },
      locale: defaultLocale,
    }))

  return {
    paths: staticPaths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection[]>
> = async ({ params, locale, preview = false }) => {
  const { entry } = params || {}

  if (!entry || Array.isArray(entry)) {
    return {
      notFound: true,
    }
  }

  const articlePath = `${ROUTE_BASE_ARTICLE}/${entry}`

  const siteConfiguration = await getSiteConfiguration(locale, preview)
  const pageData = await getRouteByPath(articlePath, locale, preview)

  if (!pageData || pageData.type !== 'article') {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...pageData,
      siteConfiguration,
    },
    revalidate: DEFAULT_REVALIDATE_TIME,
  }
}

export default InsightEntryPage
